import dayjs from 'dayjs';
import React from 'react';
import {useSearchParams} from 'react-router-dom';
import MuiAvatar from '@mui/material/Avatar';
import {Body, designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import {
  ContentSubmission,
  ContentSubmissionVersion,
  CONTENT_SUBMISSION_KIND,
  CONTENT_SUBMISSION_STATUS,
} from '@/types/contentSubmission';
import {MEDIA_ORIENTATIONS} from '@/types/mediaOrientation';
import ContentNavigation from '@/components/content-navigation';
import CreatorNameLink from '@/components/creator-name-link';
import {PROFILE_SOURCE} from '@/components/creator-profile/config/profileSources';
import {PROFILE_SOURCE_QUERY_PARAM} from '@/components/creator-profile/hooks/use-profile-source';
import PostProductionGenerationsList from '@/components/post-production-generations-list';
import useMediaOrientation from '@/hooks/use-media-orientation';
import styles from './ContentSubmissionPreview.module.scss';

export interface ContentSubmissionPreviewProps {
  testID?: string;
  contentSubmission: ContentSubmission;
  version: ContentSubmissionVersion;
  isOffPlatform?: boolean;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
}

function MediaPanel({version}: {version: ContentSubmissionVersion}) {
  const orientation = useMediaOrientation(version.thumbnailUrl);

  if (!version.url) {
    return null;
  }

  if (orientation === null) {
    return <div className={styles.mediaWrapperLoading} />;
  }

  const {orientation: dir, aspectRatio} = orientation;
  const wrapperClass =
    dir === MEDIA_ORIENTATIONS.PORTRAIT
      ? styles.mediaWrapperPortrait
      : styles.mediaWrapperLandscape;
  const wrapperStyle = {aspectRatio};

  if (version.kind === 'video') {
    return (
      <div className={wrapperClass} style={wrapperStyle}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video src={version.url} className={styles.videoContainer} controls />
      </div>
    );
  }

  return (
    <div className={wrapperClass} style={wrapperStyle}>
      <img
        className={styles.image}
        src={version.url ?? version.thumbnailUrl}
        alt=""
      />
    </div>
  );
}

const MENTION_HASHTAG_REGEX = /(@\w+|#\w+)/g;

function renderCaption(text: string) {
  let offset = 0;
  return text.split(MENTION_HASHTAG_REGEX).map((part) => {
    const key = `${offset}-${part}`;
    offset += part.length;
    if (/^[@#]\w+/.test(part)) {
      return (
        <span key={key} className={styles.captionLink}>
          {part}
        </span>
      );
    }
    return part;
  });
}

function CreatorDetailsPanel({
  contentSubmission,
  version,
  profileLink,
}: {
  contentSubmission: ContentSubmission;
  version: ContentSubmissionVersion;
  profileLink?: string;
}) {
  const profile = version.profile ?? contentSubmission.profile;
  const imageUrl = profile?.imageUrl ?? '';
  const initials = profile?.displayName
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const uploadDate = version.submittedAt
    ? dayjs(version.submittedAt).format(MONTH_SHORT_DAY_YEAR_FORMAT)
    : null;
  const caption = version.note;
  const isAcceptedVideo =
    contentSubmission.aasmState === CONTENT_SUBMISSION_STATUS.APPROVED &&
    contentSubmission.kind === CONTENT_SUBMISSION_KIND.VIDEO;

  return (
    <div className={styles.detailsPanel}>
      {profile && (
        <div className={styles.creatorInfo}>
          <MuiAvatar
            src={imageUrl}
            alt={profile.displayName}
            sx={{width: 40, height: 40}}
          >
            {initials}
          </MuiAvatar>
          <CreatorNameLink
            name={profile.displayName}
            link={profileLink}
            size="lg"
          />
        </div>
      )}
      <div className={styles.captionSection}>
        {caption ? (
          <Body
            size="md"
            className={styles.caption}
            color={designSystemToken('semantic.fg.primary')}
          >
            {renderCaption(caption)}
          </Body>
        ) : null}
        {uploadDate ? (
          <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
            {translate('uploaded')} {uploadDate}
          </Body>
        ) : null}
      </div>
      {isAcceptedVideo ? (
        <PostProductionGenerationsList
          campaignId={contentSubmission.campaign?.id}
          assetId={contentSubmission.id}
          creatorId={profile?.id}
        />
      ) : null}
    </div>
  );
}

function ContentSubmissionPreview({
  testID = 'content-submission-preview',
  contentSubmission,
  version,
  isOffPlatform = false,
  onNavigatePrevious,
  onNavigateNext,
}: ContentSubmissionPreviewProps) {
  const [searchParams] = useSearchParams();
  const profile = version.profile ?? contentSubmission.profile;

  const profileLink =
    profile?.id && !isOffPlatform
      ? `?${new URLSearchParams([
          ...searchParams.entries(),
          ['creatorProfileId', profile.id],
          [PROFILE_SOURCE_QUERY_PARAM, PROFILE_SOURCE.ASSETS],
        ]).toString()}`
      : undefined;

  return (
    <div className={styles.container} data-testid={testID}>
      <div className={styles.navigationOverlay}>
        <ContentNavigation
          onPrevious={onNavigatePrevious}
          onNext={onNavigateNext}
        />
      </div>
      <div className={styles.card}>
        <MediaPanel version={version} />
        <CreatorDetailsPanel
          contentSubmission={contentSubmission}
          version={version}
          profileLink={profileLink}
        />
      </div>
    </div>
  );
}

export default ContentSubmissionPreview;
